import { type FC, useEffect, useState } from "react";
import type { FileRejection } from "react-dropzone";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { z } from "zod";

import { Box, Button, ButtonGroup, Collapse, FormControl, FormErrorMessage } from "@chakra-ui/react";
import { ASSET_VERIFICATION, INCOME_VERIFICATION, INVESTOR_ACCREDITATION_OPTIONS, PRO_LETTER } from "@equidefi/shared";
import { DROPZONE_ACCEPTED_TYPES, RESTRICTED_CONTENT_TYPES } from "@equidefi/constants/files";
import { BannerAlert, Heading, Input, RadioGroup, Text } from "@equidefi/ui";
import { Icon } from "@equidefi/ui/icon";
import { zodResolver } from "@hookform/resolvers/zod";

import { Dropzone } from "../Dropzone";

export const ACCREDITATION_HELPER_TEXT = {
  [PRO_LETTER]: {
    text: "Recommended. Pro letter is a CPA, attorney, registered investment advisor, or broker-dealer letter on their letterhead, attesting to your status as an accredited investor based upon their knowledge of your income or your net worth. This letter must be dated within the prior 60 days.",
    header: "Accredited Investor Verification Letter",
  },
  [INCOME_VERIFICATION]: {
    text: "Income verification documents (such as IRS Forms 1040 or W-2) for the Company to review evidence of your income for the prior two years.",
    header: "Income Verification",
  },
  [ASSET_VERIFICATION]: {
    text: "Asset verification documents (such as bank or brokerage statements dated within the past 60 days), which the Company will review. If you use this method the Company will request a credit report from a national credit reporting agency to verify your debt, obtain a letter from you verifying that no debt exists other than what appears on the credit report, and calculate your estimated net worth to assist the Company in making a determination.",
    header: "Asset Verification",
  },
};

export const ACCREDITATION_FILE_OPTIONS = Object.entries(ACCREDITATION_HELPER_TEXT).map(([key, { header }]) => ({
  label: header,
  value: key,
}));

const fileFormSchema = z.object({
  name: z.string().max(256),
  type: z.enum([PRO_LETTER, INCOME_VERIFICATION, ASSET_VERIFICATION]),
  filetype: z.string().optional().nullable(),
});

type FileFormValues = z.infer<typeof fileFormSchema>;

export type AddAccreditationFileProps = {
  zoneText?: string; // Text to show in the dropzone when no file is selected
  icon?: React.ReactNode; // Icon to show in the dropzone
  onSubmit: (data: FileFormValues & { files: File[] }) => Promise<void>;
  isLoading?: boolean;
  descriptors?: boolean; // Whether to show the file type explanations
};

export const FormDebugger = ({ visible = false }) => {
  const { formState, watch } = useFormContext();

  if (process.env.NODE_ENV === "production") return null;
  if (!visible) return null;

  const data = watch();
  const { errors, isDirty } = formState;

  return (
    <Box pos="fixed" bottom={4} right={4} p={4} borderRadius="md" bg="gray.200" zIndex="overlay">
      <Heading fontSize="sm" mb={2}>
        Form Debugger
      </Heading>
      <Box textStyle="context" fontSize="0.65rem">
        <pre>{JSON.stringify({ data, errors, isDirty }, null, 2)}</pre>
      </Box>
    </Box>
  );
};

export const AddAccreditationFile: FC<AddAccreditationFileProps> = ({
  zoneText = "Upload New File",
  icon = <Icon.Upload size="1.2em" />,
  isLoading = false,
  onSubmit = () => {},
  descriptors = true,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const form = useForm<FileFormValues>({
    resolver: zodResolver(fileFormSchema),
    defaultValues: {
      name: "",
      filetype: null,
    },
  });

  const handleSubmit = async (data: FileFormValues) => {
    if (!files) return;

    await onSubmit({ ...data, files: files });

    setFiles([]);
    form.reset();
  };

  const {
    formState: { touchedFields: touched, errors },
  } = form;

  const type = form.watch("type");

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Box mb={4}>
          <RadioGroup
            options={INVESTOR_ACCREDITATION_OPTIONS}
            errors={touched.type && errors}
            onChange={(value) => form.setValue("type", value as FileFormValues["type"])}
            value={form.getValues("type")}
            defaultValue={form.formState.defaultValues?.type}
          />
        </Box>

        {descriptors && ACCREDITATION_HELPER_TEXT[type] && (
          <BannerAlert
            status="info"
            titleLayout="column"
            hideIcon={true}
            mb="6"
            p={4}
            title={ACCREDITATION_HELPER_TEXT[type].header}
            description={<Text m="0">{ACCREDITATION_HELPER_TEXT[type].text}</Text>}
          />
        )}
        <input type="hidden" {...form.register("type")} />
        <input type="hidden" {...form.register("filetype")} />

        {files.length === 0 && (
          <BannerAlert status="warning" mb="6" p={4} description="Add at least one file to continue. " />
        )}

        <Box my={4}>
          <Dropzone
            accept={DROPZONE_ACCEPTED_TYPES}
            onDrop={(acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
              form.clearErrors("root");

              for (const rejectedFile of rejectedFiles) {
                let error: string | null = null;

                const errorCodes = rejectedFiles.flatMap((file) => file.errors.map((e) => e.code));

                if (errorCodes.includes("file-invalid-types")) {
                  const extension = rejectedFile.file.name.split(".").pop();
                  error = `You cannot upload a ${extension} file type, please upload one of these supported types: ${Object.keys(
                    RESTRICTED_CONTENT_TYPES,
                  ).join(", ")}`;
                } else if (errorCodes.includes("too-many-files")) {
                  error = "You can only upload 1 file at a time.";
                }

                if (error !== null) {
                  form.setError("root", { type: "server", message: error });
                  return;
                }
              }

              setFiles(acceptedFiles);
            }}
            zoneText={files.length > 0 ? files.map((f) => f.name) : "Add New File"}
            multiple
          />
          <FormControl isInvalid={!!errors.root}>
            <FormErrorMessage>{errors.root?.message}</FormErrorMessage>
          </FormControl>
        </Box>

        <ButtonGroup colorScheme="purple">
          <Button
            rightIcon={<Icon.Upload size="1em" />}
            isLoading={isLoading}
            isDisabled={files.length === 0 || !type}
            type="submit">
            Upload
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              form.reset();
              setFiles([]);
            }}>
            Reset
          </Button>
        </ButtonGroup>

        {/* <FormDebugger /> */}
      </form>
    </FormProvider>
  );
};
